import { useQuery } from '@tanstack/react-query';
import { endOfWeek, isMonday } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { Cookie, CookieSetOptions } from 'universal-cookie';

import { UserTeam } from '../../../hooks/useUserTeam';
import { TeamIdentity } from '../../../types';
import { TTeamWiseEmployeeData } from '../../../types/analyticsTypes';
import { calculatePercentage, countWorkingDays } from '../../../utils';
import { calculateDateRanges } from '../../../utils/dateUtils';
import { mapLeaves, mapCheckIns } from '../../../utils/mappingUtils';
import { getGranularCheckInData } from '../queries/getGranularCheckInData';

const calculateCheckInsAndWorkingDays = (
  individualCheckIns: TTeamWiseEmployeeData,
): {
  totalCheckIns: number;
  totalWorkingDaysSum: number;
} => {
  return Object.values(individualCheckIns).reduce(
    (acc, category) => {
      // Process each individual entry within the category
      Object.values(category).forEach(
        ({ totalWorkingDays, midDayCheckIns, endDayCheckIns }) => {
          acc.totalCheckIns += midDayCheckIns + endDayCheckIns;
          acc.totalWorkingDaysSum += totalWorkingDays;
        },
      );
      return acc;
    },
    { totalCheckIns: 0, totalWorkingDaysSum: 0 },
  );
};

export function useAnalyticsData(
  userData: UserTeam,
  teams: TeamIdentity[],
  cookies: { notifyAnalytics?: any },
  setCookie: (
    name: 'notifyAnalytics',
    value: Cookie,
    options?: CookieSetOptions,
  ) => void,
): {
  notifyAnalytics: boolean;
} {
  const shouldFetchData = useMemo(() => {
    return Object.values(
      (cookies?.notifyAnalytics as Record<string, boolean>) || {},
    ).some(Boolean);
  }, [cookies?.notifyAnalytics]);

  useEffect(() => {
    if (!cookies.notifyAnalytics) {
      const now = new Date();
      const nextSunday = endOfWeek(now, { weekStartsOn: 1 });
      nextSunday.setHours(23, 59, 59, 999);
      const cookieValue = JSON.stringify({
        notifyMonday: true,
        notifyRestOfWeek: true,
      });
      setCookie('notifyAnalytics', cookieValue, {
        expires: nextSunday,
        path: '/',
      });
    } else if (
      (cookies.notifyAnalytics as Record<string, boolean>).notifyMonday &&
      !isMonday(new Date())
    ) {
      setCookie(
        'notifyAnalytics',
        JSON.stringify({
          ...(cookies.notifyAnalytics as Record<string, boolean>),
          notifyMonday: false,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { thisPeriodStart, thisPeriodEnd } = calculateDateRanges(
    isMonday(new Date()) ? 'last_week' : 'this_week',
  );
  const team =
    teams.find((currentTeam) => currentTeam.id === userData?.teamId)?.name ||
    '';

  const { data: checkInData } = useQuery({
    queryKey: ['checkInData', userData?.teamId],
    queryFn: () =>
      getGranularCheckInData(
        thisPeriodStart,
        thisPeriodEnd,
        team,
        userData?.teamId || '',
        userData?.role === 'manager' || userData?.role === 'partner'
          ? undefined
          : userData?.employeeId || '',
      ),
    enabled: !!userData?.teamId && shouldFetchData,
  });
  const { leavesMap, todayOooMap } = mapLeaves(
    checkInData?.leaves || [],
    thisPeriodStart,
    thisPeriodEnd,
  );
  const individualCheckIns = mapCheckIns(
    checkInData?.aggregatedCheckIns || [],
    leavesMap,
    todayOooMap,
    countWorkingDays(thisPeriodStart, thisPeriodEnd),
  );
  const { totalCheckIns, totalWorkingDaysSum } =
    calculateCheckInsAndWorkingDays(individualCheckIns);
  const result = calculatePercentage(totalCheckIns, totalWorkingDaysSum * 2);
  return {
    notifyAnalytics: result < 90,
  };
}
